import { AnimationInTypes } from '../../Enums/AnimationInTypes';
import { Directions } from '../../Enums/Directions';
import { RegionTypes } from '../../Enums/RegionTypes';
import KeyFrameHelper, { KeyFrame } from '../../Helpers/KeyFrames';
import { createCirclePath, createRectanglePath } from '../../Helpers/pathFunctions';
import { Position } from '../../Models/Shared/Position';
import { BaseAnimationIn } from './BaseAnimation';
import { type BaseVisualElement } from '../Elements/BaseVisualElement';
import { calculateWipe } from './wipeCalculation';
import { type AnimationChanges, type AnimationTarget, mergeAnimationChanges } from './AnimationChanges';
import {
    type AnimationInConfig,
    type BlurInAnimationConfig,
    type FadeBlurInAnimationConfig,
    type FadeInAnimationConfig,
    type FadeMoveInAnimationConfig,
    type FadeScaleInAnimationConfig,
    type IrisInAnimationConfig,
    type MoveInAnimationConfig,
    type PopInAnimationConfig,
    type ScaleInAnimationConfig,
    type WipeInAnimationConfig,
} from './types';

export type AnimationIn =
    | FadeInAnimation
    | MoveInAnimation
    | ScaleInAnimation
    | BlurInAnimation
    | IrisInAnimation
    | WipeInAnimation
    | PopInAnimation
    | FadeMoveInAnimation
    | FadeScaleInAnimation
    | FadeBlurInAnimation;

export class FadeInAnimation extends BaseAnimationIn<FadeInAnimationConfig> {
    compute(frameIndex: number): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const finalOpacity = this.el.opacity;
        const startOpacity = this.config.opacity || 0;
        const newOpacity = this.timingFn(
            frameIndex - this.startFrame,
            startOpacity,
            finalOpacity - startOpacity,
            this.duration - 1,
        );

        return { opacity: newOpacity };
    }
}

/**
 * MoveInAnimation animates the content of an element from outside the canvas to the element position.
 */

export class MoveInAnimation extends BaseAnimationIn<MoveInAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const direction = this.config.direction;
        const distance = this.config.distance;
        const mask = this.config.mask;
        const finalPosition = target.getAbsContentPosition();
        const currentDimension = target.contentBox.dimension;
        const canvasDimension = this.el.canvasDimension;
        let startPosition = new Position(finalPosition.getX(), finalPosition.getY());

        switch (direction) {
            case Directions.UP: {
                startPosition.setY(
                    finalPosition.getY() + (canvasDimension.getHeight() - finalPosition.getY()) * distance,
                );
                break;
            }

            case Directions.DOWN: {
                startPosition.setY(
                    finalPosition.getY() - (currentDimension.getHeight() + finalPosition.getY()) * distance,
                );
                break;
            }

            case Directions.RIGHT: {
                startPosition.setX(
                    finalPosition.getX() - (currentDimension.getWidth() + finalPosition.getX()) * distance,
                );
                break;
            }

            case Directions.LEFT: {
                startPosition.setX(
                    finalPosition.getX() + (canvasDimension.getWidth() - finalPosition.getX()) * distance,
                );
                break;
            }
        }

        const newX = this.timingFn(
            frameIndex - this.startFrame,
            startPosition.getX(),
            finalPosition.getX() - startPosition.getX(),
            this.duration - 1,
        );
        const newY = this.timingFn(
            frameIndex - this.startFrame,
            startPosition.getY(),
            finalPosition.getY() - startPosition.getY(),
            this.duration - 1,
        );

        const translateX = finalPosition.getX() - newX;
        const translateY = finalPosition.getY() - newY;
        const changes: AnimationChanges = {};

        if (mask && mask !== 'false') {
            const contentOffsetX = target.contentBox.position.getX();
            const contentOffsetY = target.contentBox.position.getY();
            const BBWidth = target.boundingBox.dimension.width;
            const BBHeight = target.boundingBox.dimension.height;

            switch (direction) {
                case Directions.UP:
                    changes.clipPath = createRectanglePath(
                        0,
                        translateY,
                        BBWidth - contentOffsetX,
                        BBHeight - contentOffsetY,
                    );
                    break;
                case Directions.DOWN:
                    changes.clipPath = createRectanglePath(
                        0 - contentOffsetX,
                        translateY - contentOffsetY,
                        BBWidth + contentOffsetX,
                        BBHeight + contentOffsetY,
                    );
                    break;
                case Directions.LEFT:
                    changes.clipPath = createRectanglePath(
                        translateX,
                        0,
                        BBWidth - contentOffsetX,
                        BBHeight - contentOffsetY,
                    );
                    break;
                case Directions.RIGHT:
                    changes.clipPath = createRectanglePath(
                        translateX - contentOffsetX,
                        0 - contentOffsetY,
                        BBWidth + contentOffsetX,
                        BBHeight + contentOffsetY,
                    );
                    break;
            }
        }

        // translate to relative box position
        changes.boxPosition = {
            x: target.boundingBox.position.getX() - translateX,
            y: target.boundingBox.position.getY() - translateY,
        };
        changes.translatedX = translateX;
        changes.translatedY = translateY;
        // set motion blur if enabled
        changes.motionBlur = target.motionBlur || (this.config.motionBlur ?? false);

        return changes;
    }
}

export class ScaleInAnimation extends BaseAnimationIn<ScaleInAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const scaleFrom = this.config.scale;
        const scaleTo = 1;
        const newScale = this.timingFn(frameIndex - this.startFrame, scaleFrom, scaleTo - scaleFrom, this.duration - 1);

        // set motion blur if enabled
        return { scale: newScale, motionBlur: target.motionBlur || (this.config.motionBlur ?? false) };
    }
}

class BlurInAnimation extends BaseAnimationIn<BlurInAnimationConfig> {
    compute(frameIndex: number): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const blurFrom = this.config.blur;
        const blurTo = 0;
        const newBlur = this.timingFn(frameIndex - this.startFrame, blurFrom, blurTo - blurFrom, this.duration - 1);

        return { blur: newBlur };
    }
}

class IrisInAnimation extends BaseAnimationIn<IrisInAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        // The transformed region is used to center the effect, while the content is used to determine the radius
        // TODO: this might fail when combined with scaling
        const contentRegion = target.getRegion(RegionTypes.TRANSFORMED);
        const regionWidth = contentRegion.getWidth();
        const regionHeight = contentRegion.getHeight();
        // const contentWidth = compEl.contentBox.dimension.getWidth();
        // const contentHeight = compEl.contentBox.dimension.getHeight();
        const ox = contentRegion.left;
        const oy = contentRegion.top;

        const distance = Math.sqrt((regionWidth / 2) ** 2 + (regionHeight / 2) ** 2);
        const newRadius = this.timingFn(frameIndex - this.startFrame, 0, distance, this.duration - 1);

        return {
            clipPath: createCirclePath(ox + regionWidth / 2, oy + regionHeight / 2, newRadius),
            // set motion blur if enabled
            motionBlur: target.motionBlur || (this.config.motionBlur ?? false),
        };
    }
}

class WipeInAnimation extends BaseAnimationIn<WipeInAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const direction = this.config.direction;
        const softness = this.config.softness;

        const contentRegion = target.getRegion(RegionTypes.TRANSFORMED);
        const w = contentRegion.getWidth();
        const h = contentRegion.getHeight();
        const ox = contentRegion.left;
        const oy = contentRegion.top;

        const p = this.timingFn(frameIndex - this.startFrame, 0, 1, this.duration - 1); // get distance percentage

        const { clipPath, clipSoftness } = calculateWipe(direction, softness, { ox, oy, w, h, p });

        // set motion blur if enabled
        return { clipPath, clipSoftness, motionBlur: target.motionBlur || (this.config.motionBlur ?? false) };
    }
}

class PopInAnimation extends BaseAnimationIn<PopInAnimationConfig> {
    // scale to 110% in 80% of time then to 100% in the remaining 20%;
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const keyFrameHelper = new KeyFrameHelper([
            new KeyFrame(0.0, 0.0), // scale is 0.0 at 0%
            new KeyFrame(0.8, 1.1), // scale to 1.1 at 80%
            new KeyFrame(1.0, 1.0), // scale to 1.0 at 100%
        ]);
        // get percentage of the animation finished
        const p = this.timingFn(frameIndex - this.startFrame, 0, 1, this.duration - 1); // get distance percentage

        // apply percentage to relative keyframes
        const newScale = keyFrameHelper.getValueLinear(p);

        // set motion blur if enabled
        return { scale: newScale, motionBlur: target.motionBlur || (this.config.motionBlur ?? false) };
    }
}

class FadeMoveInAnimation extends BaseAnimationIn<FadeMoveInAnimationConfig> {
    fadeInAnimation!: FadeInAnimation;

    moveInAnimation!: MoveInAnimation;

    constructor(config: FadeMoveInAnimationConfig, el: BaseVisualElement) {
        super(config, el);
        this.fadeInAnimation = new FadeInAnimation(config, el);
        this.moveInAnimation = new MoveInAnimation(config, el);
    }

    update(config: FadeMoveInAnimationConfig): void {
        super.update(config);
        this.fadeInAnimation.update(config);
        this.moveInAnimation.update(config);
    }

    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }
        const changes = mergeAnimationChanges(
            this.fadeInAnimation.compute(frameIndex),
            this.moveInAnimation.compute(frameIndex, target),
        );
        // set motion blur if enabled
        changes.motionBlur = (changes.motionBlur ?? target.motionBlur) || (this.config.motionBlur ?? false);

        return changes;
    }
}

class FadeScaleInAnimation extends BaseAnimationIn<FadeScaleInAnimationConfig> {
    fadeInAnimation!: FadeInAnimation;

    scaleInAnimation!: ScaleInAnimation;

    constructor(config: FadeScaleInAnimationConfig, el: BaseVisualElement) {
        super(config, el);
        this.fadeInAnimation = new FadeInAnimation(config, el);
        this.scaleInAnimation = new ScaleInAnimation(config, el);
    }

    update(config: FadeScaleInAnimationConfig): void {
        super.update(config);
        this.fadeInAnimation.update(config);
        this.scaleInAnimation.update(config);
    }

    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }
        const changes = mergeAnimationChanges(
            this.fadeInAnimation.compute(frameIndex),
            this.scaleInAnimation.compute(frameIndex, target),
        );
        // set motion blur if enabled
        changes.motionBlur = (changes.motionBlur ?? target.motionBlur) || (this.config.motionBlur ?? false);

        return changes;
    }
}

class FadeBlurInAnimation extends BaseAnimationIn<FadeBlurInAnimationConfig> {
    fadeInAnimation: FadeInAnimation;

    blurInAnimation: BlurInAnimation;

    constructor(config: FadeBlurInAnimationConfig, el: BaseVisualElement) {
        super(config, el);
        this.fadeInAnimation = new FadeInAnimation(config, el);
        this.blurInAnimation = new BlurInAnimation(config, el);
    }

    update(config: FadeBlurInAnimationConfig): void {
        super.update(config);
        this.fadeInAnimation.update(config);
        this.blurInAnimation.update(config);
    }

    compute(frameIndex: number): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        return mergeAnimationChanges(
            this.fadeInAnimation.compute(frameIndex),
            this.blurInAnimation.compute(frameIndex),
        );
    }
}

// ------------------------------------------------------------------------------

export const createAnimationIn = (config: AnimationInConfig, el: BaseVisualElement): AnimationIn | null => {
    if (!el.animationIn) {
        const elementDuration = el.duration;
        const animationOutDuration = el.animationOut?.duration ?? 0;
        const freeDuration = elementDuration - animationOutDuration;
        config.duration = freeDuration > 0 ? Math.min(freeDuration, config.duration) : config.duration;
    }

    if (!config) {
        return null;
    }

    switch (config.type) {
        case AnimationInTypes.FADE_IN:
            return new FadeInAnimation(config, el);

        case AnimationInTypes.MOVE_IN:
            return new MoveInAnimation(config, el);

        case AnimationInTypes.FADE_MOVE_IN:
            return new FadeMoveInAnimation(config, el);

        case AnimationInTypes.SCALE_IN:
            return new ScaleInAnimation(config, el);

        case AnimationInTypes.FADE_SCALE_IN:
            return new FadeScaleInAnimation(config, el);

        case AnimationInTypes.BLUR_IN:
            return new BlurInAnimation(config, el);

        case AnimationInTypes.FADE_BLUR_IN:
            return new FadeBlurInAnimation(config, el);

        case AnimationInTypes.IRIS_IN:
            return new IrisInAnimation(config, el);

        case AnimationInTypes.WIPE_IN:
            return new WipeInAnimation(config, el);

        case AnimationInTypes.POP_IN:
            return new PopInAnimation(config, el);
    }
};
